import { FF_SHOW_PAYMENT_OPTIONS } from '@/constants/featureFlags';
import ffDefaults from '@/constants/ffDefaults';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { Box, Typography } from '@montugroup/design-system';
import { styled } from '@mui/material';

interface PaymentOption {
  id: string;
  name: string;
  src: string;
  background?: string;
  enabled: boolean;
}
interface PaymentOptionsFeatureFlag {
  enabled: boolean;
  extraIds: string[];
}

export const getPaymentOptions = (extraIds: string[]): PaymentOption[] => {
  const enablePaypal = extraIds.includes('paypal');
  const enableAfterpay = extraIds.includes('afterpay');
  const enableZip = extraIds.includes('zip');
  return [
    {
      id: 'apple',
      name: 'Apple Pay',
      src: '/assets/images/pay-option-apple.svg',
      enabled: true,
    },
    {
      id: 'google',
      name: 'Google Pay',
      src: '/assets/images/pay-option-google.svg',
      enabled: true,
    },
    {
      id: 'amex',
      name: 'American Express',
      src: '/assets/images/pay-option-amex.png',
      enabled: true,
    },
    {
      id: 'mastercard',
      name: 'Mastercard',
      src: '/assets/images/pay-option-mastercard.svg',
      enabled: true,
    },
    {
      id: 'visa',
      name: 'Visa',
      src: '/assets/images/pay-option-visa.svg',
      enabled: true,
    },
    {
      id: 'paypal',
      name: 'PayPal',
      src: '/assets/images/pay-option-paypal.svg',
      enabled: enablePaypal,
    },
    {
      id: 'afterpay',
      name: 'Afterpay',
      src: '/assets/images/pay-option-afterpay.svg',
      enabled: enableAfterpay,
    },
    {
      id: 'zip',
      name: 'Zip',
      src: '/assets/images/pay-option-zip.svg',
      enabled: enableZip,
    },
  ];
};

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
}));

const PaymentOptionsList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(4),
}));

const PaymentOptionsItem = styled('li')(({ theme }) => ({
  listStyle: 'none',
  display: 'flex',
  border: '2px solid',
  borderColor: theme.palette.grey[400],
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  height: theme.spacing(9),
  width: theme.spacing(15),
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'white',
  padding: theme.spacing(0.5),
  ['& img']: {
    height: 'auto',
    maxHeight: '100%',
    width: '100%',
    objectFit: 'contain',
  },
}));

export const PaymentOptions = () => {
  const { flags } = useFeatureFlags();
  const paymentOptionsFlag: PaymentOptionsFeatureFlag =
    flags[FF_SHOW_PAYMENT_OPTIONS] || ffDefaults[FF_SHOW_PAYMENT_OPTIONS];

  if (!paymentOptionsFlag.enabled) {
    return null;
  }

  const paymentOptions = getPaymentOptions(paymentOptionsFlag.extraIds ?? []);

  return (
    <Container>
      <Typography sx={(theme) => ({ color: theme.palette.primary.main })} variant="body2" fontWeight={600}>
        Accepting a range of payment options
      </Typography>
      <PaymentOptionsList>
        {paymentOptions.map((item) =>
          item.enabled ? (
            <PaymentOptionsItem
              key={item.id}
              sx={{ backgroundColor: item.background ?? 'white' }}
              data-testid={`pay-option-${item.id}`}
            >
              <img src={item.src} alt={item.name} title={item.name} />
            </PaymentOptionsItem>
          ) : null,
        )}
      </PaymentOptionsList>
    </Container>
  );
};
